/**
 * Timezone helpers shared by web and native: the zones a picker offers, and
 * UTC-offset arithmetic that works on both browsers and Hermes.
 */

/**
 * UTC offset of an IANA timezone at a given instant, in minutes (e.g.
 * America/New_York in July → -240). Derived numerically — format the instant
 * in the target zone and diff against UTC — because Hermes' Intl doesn't
 * implement `timeZoneName: "shortOffset"` / `"longOffset"` (it yields a bare
 * "GMT").
 *
 * Returns undefined when the engine can't format in that zone. Callers own
 * the display format ("GMT-4" vs "GMT-04:00").
 */
export const getTimezoneOffsetMinutes = (tz: string, date: Date): number | undefined => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
  } catch {
    return undefined;
  }

  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
  const [year, month, day, rawHour, minute, second] = (
    ["year", "month", "day", "hour", "minute", "second"] as const
  ).map(part);
  if (!year || !month || !day || !rawHour || !minute || !second) return undefined;

  // hour12:false can render midnight as "24" in some engines.
  const hour = rawHour === "24" ? "00" : rawHour;
  const asUTC = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  return Math.round((asUTC - date.getTime()) / 60000);
};

/**
 * Whether two zones sit at different UTC offsets at an instant. Offsets rather
 * than ids, so equivalent zones (America/Los_Angeles vs America/Vancouver)
 * don't count as different. An id the engine can't format counts as the same,
 * so an unknown zone never triggers anything.
 */
export const hasDifferentUtcOffset = (a: string, b: string, at: Date): boolean => {
  if (a === b) return false;
  const offsetA = getTimezoneOffsetMinutes(a, at);
  const offsetB = getTimezoneOffsetMinutes(b, at);
  if (offsetA === undefined || offsetB === undefined) return false;
  return offsetA !== offsetB;
};

/**
 * Every zone the runtime knows about, sorted.
 *
 * `Intl.supportedValuesOf` is the only source: it is the tz database the engine
 * was built against, so it needs no maintenance and can't drift from the ids
 * the engine will accept back.
 *
 * The catch is not a formality. Hermes' Apple-backed Intl does not implement
 * `supportedValuesOf` — verified against the engine this app ships — so on
 * native this returns the device's own zone and nothing else, and the picker is
 * a single row. Every browser implements it, so web gets the full database.
 * Giving native a real list means a tz-data dependency; there is no API that
 * enumerates zones without one.
 */
export const listTimezones = (): string[] => {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("timeZone");
    // Sorted explicitly rather than trusting the engine's order, because the
    // picker groups by region and a run out of order would repeat a heading.
    if (supported?.length) return [...supported].sort((a, b) => a.localeCompare(b));
  } catch {
    // Falls through to the local zone below.
  }
  return [Intl.DateTimeFormat().resolvedOptions().timeZone].filter(Boolean);
};
